import { useState, useMemo } from "react";
import { isBusinessEmail } from "../lib/emailUtils.ts";

const MAX_MESSAGE_LEN = 280;

export const useContactForm = () => {
  const [fullName, setFullName] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [focused, setFocused] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fieldTextStyle: React.CSSProperties = useMemo(
    () => ({
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: 16,
      lineHeight: "140%",
      letterSpacing: 0,
    }),
    []
  );

  const validate = (field: string, value: string) => {
    switch (field) {
      case "fullName":
        return value.trim() ? "" : "Full Name is required";
      case "businessEmail":
        if (!value.trim()) return "Business email is required";
        return isBusinessEmail(value)
          ? ""
          : "Please enter a valid business email address";
      case "jobTitle":
        return value.trim() ? "" : "Job Title is required";
      case "companyName":
        return value.trim() ? "" : "Company Name is required";
      case "country":
        return value ? "" : "Country is required";
      case "consent":
        return value ? "" : "Please provide consent";
      default:
        return "";
    }
  };

  const setAndValidate = (field: string, value: string) => {
    setErrors((prev) => ({ ...prev, [field]: validate(field, value) }));
  };

  const neutralBorder = "#818181";

  const baseFieldStyle = (
    field: string,
    hasValue: boolean
  ): React.CSSProperties => ({
    ...fieldTextStyle,
    // Error border takes precedence; else neutral grey when empty
    borderColor:
      errors[field] && touched[field]
        ? "#FF2706"
        : !hasValue
        ? neutralBorder
        : undefined,
    // Text glow: red when error, else blue when value entered
    textShadow:
      errors[field] && touched[field]
        ? "0px 0px 11.96px rgba(255,0,0,0.5), 0px 0px 23.92px #FF0000"
        : hasValue
        ? "0px 0px 14.45px #2972D7, 0px 0px 28.9px #2972D7"
        : undefined,
  });

  const charactersLeft = useMemo(
    () => MAX_MESSAGE_LEN - message.length,
    [message]
  );

  const isFormValid = useMemo(() => {
    return (
      Boolean(fullName.trim()) &&
      Boolean(businessEmail.trim()) &&
      isBusinessEmail(businessEmail) &&
      Boolean(jobTitle.trim()) &&
      Boolean(companyName.trim()) &&
      Boolean(country) &&
      consent
    );
  }, [fullName, businessEmail, jobTitle, companyName, country, consent]);

  const resetForm = () => {
    setFullName("");
    setBusinessEmail("");
    setJobTitle("");
    setCompanyName("");
    setCountry("");
    setMessage("");
    setConsent(false);
    setErrors({});
    setTouched({});
    setFocused(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    const currentErrors: Record<string, string> = {
      fullName: validate("fullName", fullName),
      businessEmail: validate("businessEmail", businessEmail),
      jobTitle: validate("jobTitle", jobTitle),
      companyName: validate("companyName", companyName),
      country: validate("country", country),
      consent: validate("consent", consent ? "true" : ""),
    };
    
    setErrors(currentErrors);
    setTouched({
      fullName: true,
      businessEmail: true,
      jobTitle: true,
      companyName: true,
      country: true,
      consent: true,
    });
    
    const hasError = Object.values(currentErrors).some(Boolean);
    if (hasError) return;

    // Send email via API
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10000);
    
    try {
      setIsSubmitting(true);
      const CONTACT_API_URL = "https://api.armorydefense.com/api/v1/contact-us";

      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal,
        body: JSON.stringify({
          name: fullName,
          email: businessEmail,
          subject: "Contact us request from marketing website",
          title: jobTitle,
          company: companyName,
          country: country,
          notes: message || "",
          permissions: consent,
        }),
      });

      if (!response.ok) throw new Error(`Contact API responded ${response.status}`);

      // Show confirmation and reset form fields
      setShowSuccess(true);
      resetForm();
    } catch (error) {
      console.error("Error sending email:", error);
      setErrors((prev) => ({
        ...prev,
        form: "Failed to send message. Please try again later.",
      }));
    } finally {
      try { clearTimeout(timeoutId); } catch {}
      setIsSubmitting(false);
    }
  };

  return {
    // Form state
    fullName,
    setFullName,
    businessEmail,
    setBusinessEmail,
    jobTitle,
    setJobTitle,
    companyName,
    setCompanyName,
    country,
    setCountry,
    message,
    setMessage,
    consent,
    setConsent,
    
    // UI state
    errors,
    touched,
    focused,
    setFocused,
    showSuccess,
    setShowSuccess,
    isSubmitting,
    
    // Computed values
    fieldTextStyle,
    baseFieldStyle,
    charactersLeft,
    isFormValid,
    MAX_MESSAGE_LEN,
    
    // Functions
    setAndValidate,
    setTouched,
    handleSubmit,
  };
};
